// Functions & Other Math Tools helpers: betting odds format conversion,
// parlay payouts, function evaluation/composition, inverse of a linear
// function, arithmetic/geometric sequences, polynomial long division, set
// operations, boolean truth tables, 2x2 matrix arithmetic, and absolute
// value equations. Quadratic/linear equations, systems, slope, vertex,
// discriminant, factoring, and FOIL already live in
// lib/tools/equationsExpressionsCalculators.ts; probability-based odds
// conversion and expected value already live in
// lib/tools/probabilityRandomCalculators.ts - this file avoids
// re-implementing those.
import { evaluate } from 'mathjs'
import { t } from './i18n'

function gcdTwo(a: number, b: number): number {
    a = Math.abs(a)
    b = Math.abs(b)
    return b === 0 ? a : gcdTwo(b, a % b)
}
function decimalToFractionParts(decimal: number): { num: number; denom: number } {
    const str = String(decimal)
    const dotIndex = str.indexOf('.')
    if (dotIndex === -1) {
        const g = gcdTwo(Number(str), 1) || 1
        return { num: Number(str) / g, denom: 1 / g }
    }
    const decimalPlaces = Math.min(str.length - dotIndex - 1, 6)
    const denom = Math.pow(10, decimalPlaces)
    const num = Math.round(decimal * denom)
    const g = gcdTwo(num, denom) || 1
    return { num: num / g, denom: denom / g }
}

// ============================================================
// 1. Betting Odds Format Converter (Decimal <-> Fractional <-> American)
// ============================================================
export function bettingOddsConverter(params: {
    format: unknown; decimal_value: unknown; american_value: unknown
    fraction_numerator: unknown; fraction_denominator: unknown
}): { decimal_odds: number; fractional_odds: string; american_odds: number; implied_probability_percent: number } {
    const format = String(params.format || 'decimal')
    let decimalOdds: number

    if (format === 'american') {
        const a = Number(params.american_value) || 100
        decimalOdds = a > 0 ? 1 + a / 100 : 1 + 100 / Math.abs(a)
    } else if (format === 'fractional') {
        const n = Number(params.fraction_numerator) || 1
        const d = Number(params.fraction_denominator) || 1
        decimalOdds = 1 + n / d
    } else {
        decimalOdds = Math.max(Number(params.decimal_value) || 2, 1.01)
    }

    const americanOdds = decimalOdds >= 2 ? (decimalOdds - 1) * 100 : -100 / (decimalOdds - 1)
    const { num, denom } = decimalToFractionParts(decimalOdds - 1)
    const impliedProbability = (1 / decimalOdds) * 100

    return {
        decimal_odds: decimalOdds,
        fractional_odds: `${num}/${denom}`,
        american_odds: Math.round(americanOdds),
        implied_probability_percent: impliedProbability,
    }
}

// ============================================================
// 2. Parlay (Accumulator) Odds & Payout Calculator
// ============================================================
export function parlayOddsCalculator(params: { odds_list: unknown; stake: unknown }): {
    combined_odds: number; total_payout: number; profit: number
} {
    const oddsList = String(params.odds_list || '')
        .split(/[,\s]+/)
        .map((v) => v.trim())
        .filter((v) => v.length > 0)
        .map(Number)
        .filter((v) => !isNaN(v) && v > 0)
    const stake = Math.max(Number(params.stake) || 0, 0)
    const combined = oddsList.length > 0 ? oddsList.reduce((a, b) => a * b, 1) : 1
    const payout = combined * stake
    return { combined_odds: combined, total_payout: payout, profit: payout - stake }
}

// ============================================================
// 3. Function Evaluator (Evaluate f(x) at a Given x)
// ============================================================
const INVALID_EXPRESSION_LABEL: Record<string, string> = {
    en: 'Invalid expression', ru: 'Недопустимое выражение', lv: 'Nederīga izteiksme', pl: 'Nieprawidłowe wyrażenie',
    es: 'Expresión no válida', fr: 'Expression invalide', it: 'Espressione non valida', de: 'Ungültiger Ausdruck',
}
export function functionEvaluator(params: { expression: unknown; x_value: unknown; language: unknown }): { result: number | string } {
    const expr = String(params.expression || 'x')
    const x = Number(params.x_value) || 0
    try {
        const result = evaluate(expr, { x })
        if (typeof result !== 'number' || isNaN(result) || !isFinite(result)) {
            return { result: t(INVALID_EXPRESSION_LABEL, params.language) }
        }
        return { result }
    } catch {
        return { result: t(INVALID_EXPRESSION_LABEL, params.language) }
    }
}

// ============================================================
// 4. Function Composition Calculator (f(g(x)))
// ============================================================
export function functionCompositionCalculator(params: {
    f_expression: unknown; g_expression: unknown; x_value: unknown; language: unknown
}): { g_of_x: number | string; f_of_g_of_x: number | string } {
    const fExpr = String(params.f_expression || 'x')
    const gExpr = String(params.g_expression || 'x')
    const x = Number(params.x_value) || 0
    try {
        const gVal = evaluate(gExpr, { x })
        if (typeof gVal !== 'number' || isNaN(gVal) || !isFinite(gVal)) {
            const invalid = t(INVALID_EXPRESSION_LABEL, params.language)
            return { g_of_x: invalid, f_of_g_of_x: invalid }
        }
        const fVal = evaluate(fExpr, { x: gVal })
        if (typeof fVal !== 'number' || isNaN(fVal) || !isFinite(fVal)) {
            return { g_of_x: gVal, f_of_g_of_x: t(INVALID_EXPRESSION_LABEL, params.language) }
        }
        return { g_of_x: gVal, f_of_g_of_x: fVal }
    } catch {
        const invalid = t(INVALID_EXPRESSION_LABEL, params.language)
        return { g_of_x: invalid, f_of_g_of_x: invalid }
    }
}

// ============================================================
// 5. Inverse Function Finder (Linear Functions)
// ============================================================
const NO_INVERSE_LABEL: Record<string, string> = {
    en: 'No inverse (horizontal line)', ru: 'Обратной функции нет (горизонтальная линия)', lv: 'Nav apgrieztās funkcijas (horizontāla līnija)',
    pl: 'Brak funkcji odwrotnej (linia pozioma)', es: 'Sin inversa (línea horizontal)', fr: 'Pas d\'inverse (ligne horizontale)',
    it: 'Nessuna inversa (linea orizzontale)', de: 'Keine Umkehrfunktion (waagerechte Linie)',
}
export function inverseFunctionFinder(params: { slope: unknown; intercept: unknown; language: unknown }): {
    inverse_slope: number | string; inverse_intercept: number | string
} {
    const m = Number(params.slope) || 0
    const b = Number(params.intercept) || 0
    if (m === 0) {
        const noInverse = t(NO_INVERSE_LABEL, params.language)
        return { inverse_slope: noInverse, inverse_intercept: noInverse }
    }
    return { inverse_slope: 1 / m, inverse_intercept: -b / m }
}

// ============================================================
// 6. Arithmetic Sequence Calculator
// ============================================================
export function arithmeticSequenceCalculator(params: { first_term: unknown; common_difference: unknown; n: unknown }): {
    nth_term: number; sum_of_terms: number
} {
    const a1 = Number(params.first_term) || 0
    const d = Number(params.common_difference) || 0
    const n = Math.max(Math.round(Number(params.n)) || 1, 1)
    const nthTerm = a1 + (n - 1) * d
    const sum = (n / 2) * (2 * a1 + (n - 1) * d)
    return { nth_term: nthTerm, sum_of_terms: sum }
}

// ============================================================
// 7. Geometric Sequence Calculator
// ============================================================
export function geometricSequenceCalculator(params: { first_term: unknown; common_ratio: unknown; n: unknown }): {
    nth_term: number; sum_of_terms: number
} {
    const a1 = Number(params.first_term) || 0
    const r = Number(params.common_ratio) || 0
    const n = Math.max(Math.round(Number(params.n)) || 1, 1)
    const nthTerm = a1 * Math.pow(r, n - 1)
    const sum = r === 1 ? a1 * n : (a1 * (Math.pow(r, n) - 1)) / (r - 1)
    return { nth_term: nthTerm, sum_of_terms: sum }
}

// ============================================================
// 8. Polynomial Long Division Calculator
// ============================================================
function parseCoefficients(raw: unknown): number[] {
    const coeffs = String(raw ?? '')
        .split(/[,\s]+/)
        .map((v) => v.trim())
        .filter((v) => v.length > 0)
        .map(Number)
        .filter((v) => !isNaN(v))
    return coeffs.length > 0 ? coeffs : [1]
}
function coeffsToPolynomialString(coeffs: number[]): string {
    const degreeStart = coeffs.length - 1
    const terms: string[] = []
    coeffs.forEach((c, i) => {
        if (c === 0) return
        const degree = degreeStart - i
        const absC = Math.abs(c)
        const sign = c < 0 ? '-' : terms.length === 0 ? '' : '+'
        let term: string
        if (degree === 0) term = `${absC}`
        else if (degree === 1) term = absC === 1 ? 'x' : `${absC}x`
        else term = absC === 1 ? `x^${degree}` : `${absC}x^${degree}`
        terms.push(terms.length === 0 ? `${sign}${term}` : `${sign} ${term}`)
    })
    return terms.length === 0 ? '0' : terms.join(' ')
}
const DIVISION_BY_ZERO_LABEL: Record<string, string> = {
    en: 'undefined (division by zero)', ru: 'не определено (деление на ноль)', lv: 'nav definēts (dalīšana ar nulli)',
    pl: 'nieokreślone (dzielenie przez zero)', es: 'indefinido (división por cero)', fr: 'indéfini (division par zéro)',
    it: 'non definito (divisione per zero)', de: 'undefiniert (Division durch null)',
}
export function polynomialLongDivisionCalculator(params: { dividend_coefficients: unknown; divisor_coefficients: unknown; language: unknown }): {
    quotient: string; remainder: string
} {
    const dividend = parseCoefficients(params.dividend_coefficients)
    const divisor = parseCoefficients(params.divisor_coefficients)
    if (divisor.every((c) => c === 0)) {
        const errText = t(DIVISION_BY_ZERO_LABEL, params.language)
        return { quotient: errText, remainder: errText }
    }

    let rem = dividend.slice()
    const divisorDegree = divisor.length - 1
    const quotientLength = Math.max(rem.length - divisor.length + 1, 0)
    const quotient = new Array(quotientLength).fill(0)

    while (rem.length - 1 >= divisorDegree && !(rem.length === 1 && rem[0] === 0)) {
        const leadCoeff = rem[0] / divisor[0]
        const degreeDiff = rem.length - 1 - divisorDegree
        if (degreeDiff < 0) break
        quotient[quotient.length - 1 - degreeDiff] = leadCoeff
        for (let i = 0; i < divisor.length; i++) rem[i] -= leadCoeff * divisor[i]
        rem.shift()
        while (rem.length > 0 && Math.abs(rem[0]) < 1e-9) rem.shift()
        if (rem.length === 0) rem = [0]
    }

    return { quotient: coeffsToPolynomialString(quotient), remainder: coeffsToPolynomialString(rem) }
}

// ============================================================
// 9. Set Operations Calculator
// ============================================================
function parseSet(raw: unknown): string[] {
    const seen = new Set<string>()
    const result: string[] = []
    for (const item of String(raw ?? '').split(',')) {
        const trimmed = item.trim()
        if (trimmed.length > 0 && !seen.has(trimmed)) {
            seen.add(trimmed)
            result.push(trimmed)
        }
    }
    return result
}
function formatSet(items: string[]): string {
    const allNumeric = items.every((v) => !isNaN(Number(v)) && v !== '')
    const sorted = allNumeric ? [...items].sort((a, b) => Number(a) - Number(b)) : [...items].sort()
    return sorted.length > 0 ? `{${sorted.join(', ')}}` : '{}'
}
export function setOperationsCalculator(params: { set_a: unknown; set_b: unknown }): {
    union_result: string; intersection_result: string; a_minus_b_result: string; b_minus_a_result: string
} {
    const a = parseSet(params.set_a)
    const b = parseSet(params.set_b)
    const union = Array.from(new Set([...a, ...b]))
    const intersection = a.filter((x) => b.includes(x))
    const aMinusB = a.filter((x) => !b.includes(x))
    const bMinusA = b.filter((x) => !a.includes(x))
    return {
        union_result: formatSet(union),
        intersection_result: formatSet(intersection),
        a_minus_b_result: formatSet(aMinusB),
        b_minus_a_result: formatSet(bMinusA),
    }
}

// ============================================================
// 10. Boolean Logic Truth Table Generator
// ============================================================
type BoolToken = { type: 'VAR' | 'OP' | 'PAREN'; value: string }
type BoolNode =
    | { type: 'VAR'; name: string }
    | { type: 'NOT'; operand: BoolNode }
    | { type: 'AND' | 'OR' | 'XOR'; left: BoolNode; right: BoolNode }

function tokenizeBoolExpr(expr: string): BoolToken[] {
    const normalized = expr
        .toUpperCase()
        .replace(/\bAND\b/g, '&')
        .replace(/\bOR\b/g, '|')
        .replace(/\bNOT\b/g, '!')
        .replace(/\bXOR\b/g, '^')
    const tokens: BoolToken[] = []
    for (const ch of normalized) {
        if (/[A-Z]/.test(ch)) tokens.push({ type: 'VAR', value: ch })
        else if (ch === '&' || ch === '|' || ch === '!' || ch === '^') tokens.push({ type: 'OP', value: ch })
        else if (ch === '(' || ch === ')') tokens.push({ type: 'PAREN', value: ch })
        else if (ch === ' ') continue
        else throw new Error(`Invalid character: ${ch}`)
    }
    return tokens
}
function parseBoolExpr(tokens: BoolToken[]): BoolNode {
    let pos = 0
    const peek = () => tokens[pos]
    const consume = () => tokens[pos++]

    function parseOr(): BoolNode {
        let left = parseXor()
        while (peek() && peek().type === 'OP' && peek().value === '|') {
            consume()
            left = { type: 'OR', left, right: parseXor() }
        }
        return left
    }
    function parseXor(): BoolNode {
        let left = parseAnd()
        while (peek() && peek().type === 'OP' && peek().value === '^') {
            consume()
            left = { type: 'XOR', left, right: parseAnd() }
        }
        return left
    }
    function parseAnd(): BoolNode {
        let left = parseNot()
        while (peek() && peek().type === 'OP' && peek().value === '&') {
            consume()
            left = { type: 'AND', left, right: parseNot() }
        }
        return left
    }
    function parseNot(): BoolNode {
        if (peek() && peek().type === 'OP' && peek().value === '!') {
            consume()
            return { type: 'NOT', operand: parseNot() }
        }
        return parsePrimary()
    }
    function parsePrimary(): BoolNode {
        const tok = peek()
        if (!tok) throw new Error('Unexpected end of expression')
        if (tok.type === 'VAR') {
            consume()
            return { type: 'VAR', name: tok.value }
        }
        if (tok.type === 'PAREN' && tok.value === '(') {
            consume()
            const node = parseOr()
            if (!peek() || peek().value !== ')') throw new Error('Expected closing parenthesis')
            consume()
            return node
        }
        throw new Error(`Unexpected token: ${tok.value}`)
    }

    const ast = parseOr()
    if (pos !== tokens.length) throw new Error('Unexpected trailing tokens')
    return ast
}
function evalBoolAst(node: BoolNode, vars: Record<string, boolean>): boolean {
    switch (node.type) {
        case 'VAR':
            return !!vars[node.name]
        case 'NOT':
            return !evalBoolAst(node.operand, vars)
        case 'AND':
            return evalBoolAst(node.left, vars) && evalBoolAst(node.right, vars)
        case 'OR':
            return evalBoolAst(node.left, vars) || evalBoolAst(node.right, vars)
        case 'XOR':
            return evalBoolAst(node.left, vars) !== evalBoolAst(node.right, vars)
    }
}
export function truthTableGenerator(params: { expression: unknown; variables: unknown }): { truth_table: string } {
    const varNames = String(params.variables || 'A,B')
        .split(',')
        .map((v) => v.trim().toUpperCase())
        .filter((v) => /^[A-Z]$/.test(v))
        .slice(0, 4)
    const uniqueVars = Array.from(new Set(varNames))
    if (uniqueVars.length === 0) return { truth_table: 'Invalid variables' }

    try {
        const tokens = tokenizeBoolExpr(String(params.expression || uniqueVars[0]))
        const ast = parseBoolExpr(tokens)
        const header = `${uniqueVars.join(' | ')} | Result`
        const rows: string[] = [header]
        const total = Math.pow(2, uniqueVars.length)
        for (let i = 0; i < total; i++) {
            const vars: Record<string, boolean> = {}
            uniqueVars.forEach((name, idx) => {
                vars[name] = !!((i >> (uniqueVars.length - 1 - idx)) & 1)
            })
            const result = evalBoolAst(ast, vars)
            const rowValues = uniqueVars.map((name) => (vars[name] ? 'T' : 'F'))
            rows.push(`${rowValues.join(' | ')} | ${result ? 'T' : 'F'}`)
        }
        return { truth_table: rows.join('\n') }
    } catch {
        return { truth_table: 'Invalid expression' }
    }
}

// ============================================================
// 11. 2x2 Matrix Calculator
// ============================================================
export function matrixCalculator2x2(params: {
    a11: unknown; a12: unknown; a21: unknown; a22: unknown
    b11: unknown; b12: unknown; b21: unknown; b22: unknown
    operation: unknown
}): { result_11: number; result_12: number; result_21: number; result_22: number; determinant_a: number; determinant_b: number } {
    const a11 = Number(params.a11) || 0
    const a12 = Number(params.a12) || 0
    const a21 = Number(params.a21) || 0
    const a22 = Number(params.a22) || 0
    const b11 = Number(params.b11) || 0
    const b12 = Number(params.b12) || 0
    const b21 = Number(params.b21) || 0
    const b22 = Number(params.b22) || 0
    const operation = String(params.operation || 'add')

    let result11: number, result12: number, result21: number, result22: number
    if (operation === 'subtract') {
        result11 = a11 - b11
        result12 = a12 - b12
        result21 = a21 - b21
        result22 = a22 - b22
    } else if (operation === 'multiply') {
        result11 = a11 * b11 + a12 * b21
        result12 = a11 * b12 + a12 * b22
        result21 = a21 * b11 + a22 * b21
        result22 = a21 * b12 + a22 * b22
    } else {
        result11 = a11 + b11
        result12 = a12 + b12
        result21 = a21 + b21
        result22 = a22 + b22
    }

    return {
        result_11: result11, result_12: result12, result_21: result21, result_22: result22,
        determinant_a: a11 * a22 - a12 * a21, determinant_b: b11 * b22 - b12 * b21,
    }
}

// ============================================================
// 12. Absolute Value Equation Solver
// ============================================================
const NO_SOLUTION_LABEL: Record<string, string> = {
    en: 'No real solution', ru: 'Нет действительных решений', lv: 'Nav reāla risinājuma', pl: 'Brak rozwiązania rzeczywistego',
    es: 'Sin solución real', fr: 'Aucune solution réelle', it: 'Nessuna soluzione reale', de: 'Keine reelle Lösung',
}
const ALL_REAL_NUMBERS_LABEL: Record<string, string> = {
    en: 'All real numbers', ru: 'Все действительные числа', lv: 'Visi reālie skaitļi', pl: 'Wszystkie liczby rzeczywiste',
    es: 'Todos los números reales', fr: 'Tous les nombres réels', it: 'Tutti i numeri reali', de: 'Alle reellen Zahlen',
}
export function absoluteValueEquationSolver(params: { a: unknown; b: unknown; c: unknown; language: unknown }): {
    solution_1: number | string; solution_2: number | string
} {
    const a = Number(params.a) || 0
    const b = Number(params.b) || 0
    const c = Number(params.c) || 0

    if (a === 0) {
        if (Math.abs(b) === c) {
            const allReal = t(ALL_REAL_NUMBERS_LABEL, params.language)
            return { solution_1: allReal, solution_2: allReal }
        }
        const noSolution = t(NO_SOLUTION_LABEL, params.language)
        return { solution_1: noSolution, solution_2: noSolution }
    }
    if (c < 0) {
        const noSolution = t(NO_SOLUTION_LABEL, params.language)
        return { solution_1: noSolution, solution_2: noSolution }
    }

    return { solution_1: (c - b) / a, solution_2: (-c - b) / a }
}
